import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@libs/zapi-ui/shadcn";
import type { TBlockRender, TTableBlock } from "@/zapi-frontend/shared/types/contentBlocks";
import { parseNumericCell } from "@/zapi-frontend/shared/lib/contentBlocks";
import { refForCell } from "@/zapi-frontend/shared/lib/figureRefs";
import { TableBlock } from "./_TableBlock";
import {
  BarRowsLayout,
  CHART_CURSOR_FILL,
  CHART_GRID_STROKE,
  CHART_INK,
  CHART_TRACK_FILL,
  ChartBlockFrame,
  MonoAxisTick,
  ROW_AXIS_HEIGHT,
  ROW_PITCH,
  displayValueFormatter,
  tooltipRowLabel
} from "./_ChartBlockFrame";

const BAR_SIZE = 18;

const partOnlyFormatter: typeof displayValueFormatter = (value, name, item, index, payload) =>
  item.dataKey === "rest" ? null : displayValueFormatter(value, name, item, index, payload);

type TSharePoint = {
  index: number;
  label: string;
  display: string;
  part: number;
  rest: number;
  partDisplay: string;
  figureKey?: string;
};

// Part-of-whole bars: partCols names the whole column then the part column; each row draws
// the part stacked against the remainder of its whole.
export function StackedShareBlock({ block, render }: { block: TTableBlock; render: TBlockRender }) {
  const [wholeIndex, partIndex] = render.partCols ?? [1, 2];
  const wholeCol = block.cols[wholeIndex] ?? "";
  const partCol = block.cols[partIndex] ?? "";
  const restLabel = `Rest of ${wholeCol}`;
  const data: TSharePoint[] = block.rows
    .map((row, r) => ({
      label: row[0],
      display: row[partIndex] ?? "",
      whole: parseNumericCell(row[wholeIndex] ?? ""),
      part: parseNumericCell(row[partIndex] ?? ""),
      figureKey: refForCell(block.refs, r, partIndex)?.key
    }))
    .filter(
      (
        point
      ): point is { label: string; display: string; whole: number; part: number; figureKey: string | undefined } =>
        point.whole !== null && point.part !== null
    )
    .map((point, index) => {
      const rest = Math.max(point.whole - point.part, 0);
      return {
        index,
        label: point.label,
        display: point.display,
        part: point.part,
        rest,
        partDisplay: point.display,
        figureKey: point.figureKey
      };
    });
  // Every row stays in the table; a row without both numbers simply has no bar to draw.
  if (data.length === 0) return <TableBlock block={block} />;

  const config: ChartConfig = {
    part: { label: partCol, color: CHART_INK },
    rest: { label: restLabel, color: CHART_TRACK_FILL }
  };

  return (
    <ChartBlockFrame
      label={block.label}
      note={render.note}
      cols={block.cols}
      rows={block.rows}
      refs={block.refs}
      skipped={block.rows.length - data.length}
    >
      <BarRowsLayout rows={data}>
        <ChartContainer
          config={config}
          className="aspect-auto w-full"
          style={{ height: data.length * ROW_PITCH + ROW_AXIS_HEIGHT }}
        >
          <BarChart data={data} layout="vertical" margin={{ top: 0, right: 0, bottom: 0, left: 0 }} barCategoryGap={0}>
            <CartesianGrid horizontal={false} stroke={CHART_GRID_STROKE} />
            <XAxis
              type="number"
              height={ROW_AXIS_HEIGHT}
              interval={0}
              tickLine={false}
              axisLine={false}
              tick={<MonoAxisTick />}
            />
            <YAxis type="category" dataKey="index" hide />
            <ChartTooltip
              cursor={{ fill: CHART_CURSOR_FILL }}
              content={<ChartTooltipContent labelFormatter={tooltipRowLabel} formatter={partOnlyFormatter} />}
            />
            <Bar
              name={partCol}
              dataKey="part"
              stackId="share"
              fill="var(--color-part)"
              barSize={BAR_SIZE}
              isAnimationActive={false}
            />
            <Bar
              name={restLabel}
              dataKey="rest"
              stackId="share"
              fill="var(--color-rest)"
              barSize={BAR_SIZE}
              isAnimationActive={false}
            />
          </BarChart>
        </ChartContainer>
      </BarRowsLayout>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[12px] leading-4 text-innoveraZinc-500">
        <span className="flex items-center gap-1.5">
          <span className="size-2 rounded-[2px]" style={{ backgroundColor: CHART_INK }} />
          {partCol}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="size-2 rounded-[2px]" style={{ backgroundColor: CHART_TRACK_FILL }} />
          {wholeCol}
        </span>
      </div>
    </ChartBlockFrame>
  );
}
